<template>
  <div
    ref="accordionRef"
    class="accordion-block"
    data-testid="Accordion"
  >
    <div
      v-for="(panel, index) in panels"
      :id="panel.id"
      :key="panel.id"
      class="accordion-panel"
    >
      <AccordionPanel
        :item="panel"
        :theme="config.accordion.theme"
        :globals="globals"
        :col-index="colIndex"
        :icon="config.accordion.icon_style"
        @toggle-panel="
          config.accordion.allow_multiple_open_panels ? toggleSinglePanel(panel.id) : toggleCurrentlyOpenPanel(panel.id)
        "
        @next-panel="focusNextPanel(index)"
        @previous-panel="focusPreviousPanel(index)"
        @first-panel="focusFirstPanel"
        @last-panel="focusLastPanel"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import AccordionPanel from './AccordionPanel.vue';

defineOptions({
  name: 'AccordionBlock',
});

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
  colIndex: {
    type: Number,
    required: true,
  },
  globals: {
    type: Object,
    required: true,
  },
});

const accordionRef = ref(null);
const panels = ref((cloneDeep(props.config?.accordion?.items) ?? []).map((panel) => ({ ...panel, expanded: false })));

function toggleCurrentlyOpenPanel(id) {
  panels.value = panels.value.map((panel) =>
    panel.id === id ? { ...panel, expanded: !panel.expanded } : { ...panel, expanded: false },
  );
}

function toggleSinglePanel(id) {
  panels.value = panels.value.map((panel) =>
    panel.id === id ? { ...panel, expanded: !panel.expanded } : { ...panel },
  );
}

function focusNextPanel(index) {
  const accordion = accordionRef.value;
  if (!accordion || index + 1 === accordion.children?.length) return;
  const focusedIndex = index + 1;
  const nextPanelHeader = accordion.children[focusedIndex]?.children[0]?.querySelector('button');
  if (!nextPanelHeader) return;
  nextPanelHeader.focus();
}

function focusPreviousPanel(index) {
  if (index === 0) return;
  const accordion = accordionRef.value;
  const focusedIndex = index - 1;
  const previousPanelHeader = accordion?.children[focusedIndex]?.children[0]?.querySelector('button');
  if (!previousPanelHeader) return;
  previousPanelHeader.focus();
}

function focusFirstPanel() {
  const firstPanelHeader = accordionRef.value?.children[0]?.children[0]?.querySelector('button');
  if (!firstPanelHeader) return;
  firstPanelHeader.focus();
}

function focusLastPanel() {
  const accordion = accordionRef.value;
  if (!accordion) return;
  const focusedIndex = accordion.children.length - 1;
  const lastPanelHeader = accordion.children[focusedIndex]?.children[0]?.querySelector('button');
  if (!lastPanelHeader) return;
  lastPanelHeader.focus();
}
</script>

<style lang="scss" scoped>
div.accordion-block {
  top: 40px;
  padding-bottom: 1px;
  margin: 0 auto;
}
</style>
